import { useEffect, useState } from 'react';
import { MessageSquare, Video, FolderOpen, Users, Calendar, MoreHorizontal, GitBranch, Sun, Settings, X, Mail, ExternalLink, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import type { View } from '../../App';
import { useChatStore, useTeamStore } from '../../stores';
import { adminUrl } from '../../lib/urls';
import { useLicenseFeature } from '../../hooks/useLicenseFeatures';

interface MobileBottomNavProps {
  currentView: View;
  onNavigate: (view: View) => void;
}

const items: { id: View; icon: typeof MessageSquare }[] = [
  { id: 'today',    icon: Sun },
  { id: 'chat',     icon: MessageSquare },
  { id: 'teams',    icon: Users },
  { id: 'meet',     icon: Video },
];

const moreItems: { id: View; icon: typeof MessageSquare }[] = [
  { id: 'mail',      icon: Mail },
  { id: 'calendar',  icon: Calendar },
  { id: 'files',     icon: FolderOpen },
  { id: 'workflows', icon: GitBranch },
  { id: 'settings',  icon: Settings },
];

/**
 * Phone tab bar (hidden from `md` up, where the sidebar takes over).
 * `mobile-tabbar` lets CSS hide it while the keyboard is open or a detail
 * screen (a conversation, a channel, a mail) fills the screen.
 */
export function MobileBottomNav({ currentView, onNavigate }: MobileBottomNavProps) {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const rooms = useChatStore((s) => s.rooms);
  const teams = useTeamStore((s) => s.teams);

  const chatUnread = rooms.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0);
  const teamUnread = teams.reduce((sum, t) => {
    const channelTotal = (t.channels ?? []).reduce((cs, ch) => cs + (ch.unreadCount ?? 0), 0);
    return sum + channelTotal;
  }, 0);

  const badges: Record<string, number> = { chat: chatUnread, teams: teamUnread };
  // Mail is an add-on: not listed when the license never included it
  const mailHidden = useLicenseFeature('mail') === 'hidden';
  const visibleMore = moreItems.filter(i => !(i.id === 'mail' && mailHidden));
  const isMoreActive = visibleMore.some(i => i.id === currentView);

  useEffect(() => {
    if (!showMore) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMore(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showMore]);

  return (
    <>
      {/* More: a bottom sheet with full-width rows */}
      {showMore && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setShowMore(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.more')}
            className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-nord-3/40 bg-nord-1 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-xl animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="mx-auto mb-1 h-1 w-10 rounded-full bg-nord-3" aria-hidden="true" />
            <div className="flex items-center justify-between px-2">
              <span className="text-xs font-semibold text-nord-4 uppercase tracking-wider">{t('nav.more')}</span>
              <button onClick={() => setShowMore(false)} aria-label={t('common.close')} className="flex h-11 w-11 items-center justify-center rounded-full text-nord-4 hover:text-nord-6">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <ul>
              {visibleMore.map(item => {
                const Icon = item.icon;
                const active = currentView === item.id;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => { onNavigate(item.id); setShowMore(false); }}
                      aria-current={active ? 'page' : undefined}
                      className={clsx(
                        'flex min-h-[48px] w-full items-center gap-3 rounded-lg px-3 text-left text-[15px] transition-colors',
                        active ? 'bg-nord-8/15 text-nord-8' : 'text-nord-5 active:bg-nord-2',
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className="flex-1">{t(`nav.${item.id}`)}</span>
                      <ChevronRight className="h-4 w-4 text-nord-muted" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
              <li>
                <a
                  href={adminUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-[48px] w-full items-center gap-3 rounded-lg px-3 text-[15px] text-nord-5 active:bg-nord-2"
                >
                  <ExternalLink className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="flex-1">{t('nav.admin')}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}

      <nav
        aria-label={t('nav.mobile')}
        className="mobile-tabbar fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-nord-3/40 bg-nord-1 md:hidden safe-bottom safe-x"
      >
        {items.map((item) => {
          const Icon = item.icon;
          const active = currentView === item.id;
          const badge = badges[item.id] ?? 0;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={clsx(
                'relative flex h-14 flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
                active ? 'text-nord-8' : 'text-nord-4',
              )}
              aria-current={active ? 'page' : undefined}
              aria-label={t(`nav.${item.id}`)}
            >
              <div className="relative">
                <Icon className="h-6 w-6" />
                {badge > 0 && (
                  <span className="absolute -top-1 -right-2 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-nord-11 px-0.5 text-[9px] font-bold text-white">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-medium">{t(`nav.${item.id}`)}</span>
            </button>
          );
        })}
        {/* More button */}
        <button
          onClick={() => setShowMore(!showMore)}
          className={clsx(
            'relative flex h-14 flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
            isMoreActive || showMore ? 'text-nord-8' : 'text-nord-4',
          )}
          aria-label={t('nav.more')}
          aria-expanded={showMore}
        >
          <MoreHorizontal className="h-6 w-6" />
          <span className="text-[11px] font-medium">{t('nav.more')}</span>
        </button>
      </nav>
    </>
  );
}
